import { formatFhirDate, getFhirDate } from '@utils/fhir';
import { capitalize } from '@utils/format';

import type { AllergyModelInput, AllergyModel } from 'generated/types';
import { CategoryCode, ClinicalStatusCode } from 'generated/types';

import { FhirResource, type BaseFhirFormValues } from './fhir-resource';

export type AllergyValues = Pick<
  AllergyRecord,
  | 'allergyType'
  | 'clinicalStatus'
  | 'lastKnownReaction'
  | 'reactionDescription'
  | 'responseDescription'
  | 'selfReported'
  | 'severity'
> & {
  // substance
  title: string;
  noAllergies: string | null;
  associatedResources?: string[];
};

export type FormValues = BaseFhirFormValues & AllergyValues;

export const getAllergyType = (type: string) => {
  switch (type) {
    case 'biologic':
      return CategoryCode.biologic;
    case 'environment':
      return CategoryCode.environment;
    case 'food':
      return CategoryCode.food;
    case 'medication':
    default:
      return CategoryCode.medication;
  }
};

export const getClinicalStatus = (status: string) => {
  switch (status) {
    case 'inactive':
      return ClinicalStatusCode.inactive;
    case 'resolved':
      return ClinicalStatusCode.resolved;
    case 'active':
    default:
      return ClinicalStatusCode.active;
  }
};

export const ALLERGY_SEVERITY_MAP: Record<string, string> = {
  // severity: reactionGrade
  mild: 'Mild',
  moderate: 'Moderate',
  severe: 'Severe (Anaphylactic)',
};

export const NO_KNOWN_ALLERGY_MAP: Record<string, string> = {
  NKA: 'No Known Allergies',
  NKDA: 'No Known Drug Allergies',
  NKFA: 'No Known Food Allergies',
};
export class AllergyRecord extends FhirResource {
  protected model: AllergyModel;

  public constructor(model: AllergyModel) {
    super(model);
    this.model = model;
  }

  public get recordType() {
    return this.model.__typename;
  }

  public get substance() {
    return (
      this.model.code?.text ??
      this.model.reaction
        ?.map(({ substance }) => substance?.text)
        .flat()
        .join(', ')
    );
  }

  public set substance(value: string | null | undefined) {
    this.model = {
      ...this.model,
      code: value ? { text: value } : null,
      reaction: value
        ? [
            {
              manifestation: [
                {
                  text: this.reactionGrade,
                },
              ],
              severity: this.severity,
              substance: { text: value },
              ...(this.responseDescription && {
                note: [{ text: this.responseDescription }],
              }),
              description: this.reactionDescription,
            },
          ]
        : null,
    };
  }

  // mild, moderate, severe
  public get severity() {
    return this.model.reaction
      ?.map(({ severity }) => severity)
      .flat()
      .join(', ');
  }

  public set severity(value: string | null | undefined) {
    this.model = {
      ...this.model,
      reaction: value
        ? [
            {
              manifestation: [{ text: ALLERGY_SEVERITY_MAP[value] }],
              severity: value,
              description: this.reactionDescription,
              ...(this.responseDescription && {
                note: [{ text: this.responseDescription }],
              }),
              ...(this.substance && {
                substance: {
                  text: this.substance,
                },
              }),
              ...(this.responseDescription && {
                note: [{ text: this.responseDescription }],
              }),
            },
          ]
        : null,
    };
  }

  public get criticality() {
    return this.model.criticality;
  }

  public get isSevere(): boolean {
    return this.model.reaction?.some((r) => r.severity === 'severe') ?? false;
  }

  public get allergyType() {
    return this.model.category?.join(', ');
  }

  public set allergyType(value: string | null | undefined) {
    this.model = {
      ...this.model,
      category: value ? [getAllergyType(value)] : null,
    };
  }

  public get responseDescription() {
    return this.model.reaction
      ?.map(({ note }) => note?.map(({ text }) => text))
      .flat()
      .join(', ');
  }

  public set responseDescription(value: string | null | undefined) {
    this.model = {
      ...this.model,
      reaction: value
        ? [
            {
              manifestation: [
                {
                  text: this.reactionGrade,
                },
              ],
              severity: this.severity,
              note: [
                {
                  text: value,
                },
              ],
              description: this.reactionDescription,
              ...(this.responseDescription && {
                note: [{ text: this.responseDescription }],
              }),
            },
          ]
        : null,
    };
  }

  public get reactionDescription() {
    return this.model.reaction
      ?.map(({ description }) => description)
      .join(', ');
  }

  public set reactionDescription(value: string | null | undefined) {
    this.model = {
      ...this.model,
      reaction: value
        ? [
            {
              severity: this.severity,
              manifestation: [
                {
                  text: this.reactionGrade,
                },
              ],
              description: value,
              substance: {
                text: this.substance,
              },
              ...(this.responseDescription && {
                note: [{ text: this.responseDescription }],
              }),
            },
          ]
        : null,
    };
  }

  // Friendly text of severity field: Mild, Moderate, Severe (Anaphylactic)
  public get reactionGrade() {
    return (
      this.model.reaction
        ?.map(({ severity }) =>
          severity ? ALLERGY_SEVERITY_MAP[severity] : 'Unknown'
        )
        .flat()
        .join(', ') ?? 'Unknown'
    );
  }

  public get clinicalStatus() {
    return this.model.clinicalStatus?.text;
  }

  public set clinicalStatus(value: string | null | undefined) {
    this.model = {
      ...this.model,
      ...(value && {
        clinicalStatus: {
          text: capitalize(value),
          coding: [
            {
              code: getClinicalStatus(value),
              display: capitalize(value),
              system:
                'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
            },
          ],
        },
      }),
    };
  }

  public get noAllergies() {
    const noKnownAllergy = this.model.code?.coding?.find(
      ({ code }) => code === 'NKA' || code === 'NKDA' || code === 'NKFA'
    );
    return noKnownAllergy?.code;
  }

  public set noAllergies(value: string | null | undefined) {
    this.model = {
      ...this.model,
      code: value
        ? {
            text: NO_KNOWN_ALLERGY_MAP[value],
            coding: [
              {
                code: value,
                display: NO_KNOWN_ALLERGY_MAP[value],
                system: AllergyRecord.VITALL_SYSTEM,
                userSelected: true,
              },
            ],
          }
        : null,
    };
  }

  public get lastKnownReaction() {
    return this.model.lastOccurrence;
  }

  public set lastKnownReaction(value: string | null | undefined) {
    this.model = {
      ...this.model,
      lastOccurrence: value,
    };
  }

  public getLastKnownReaction() {
    return formatFhirDate(this.model.lastOccurrence, { showTime: false });
  }

  public getLastKnownReactionDate() {
    return getFhirDate(this.model.lastOccurrence);
  }

  public getModelInput(values?: AllergyValues): AllergyModelInput {
    if (values) {
      // eslint-disable-next-line guard-for-in
      for (const key in values) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this[key] = values[key];
        } catch {
          // eslint-disable-next-line no-continue
          continue;
        }
      }
      this.substance = values.title;
    }
    const cleaned = FhirResource.getCleanInput(this.model);
    return cleaned;
  }
}
